import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Key, Plus, Edit2, Trash2, X, Eye, EyeOff, Copy, ExternalLink, Search, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function copyText(text, label) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copiado`),
    () => toast.error("Não consegui copiar")
  );
}

function CredentialModal({ cred, onClose, onSaved }) {
  const isEdit = Boolean(cred?.id);
  const [form, setForm] = useState({
    bank_name: cred?.bank_name || "",
    website: cred?.website || "",
    username: cred?.username || "",
    password: cred?.password || "",
    notes: cred?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.bank_name.trim()) { toast.error("Informe o nome do banco/sistema"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/bank-credentials/${cred.id}`, form);
      else await api.post("/bank-credentials", form);
      toast.success("Salvo");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="credential-modal">
      <div className="bg-background border border-border max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-3 flex items-center justify-between sticky top-0 bg-background z-10">
          <p className="label-eyebrow text-primary inline-flex items-center gap-2">
            <Key size={14} /> {isEdit ? "Editar acesso" : "Novo acesso de banco"}
          </p>
          <button type="button" onClick={onClose} className="text-text-secondary hover:text-primary"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label-eyebrow block mb-2">Banco / Sistema *</label>
            <input data-testid="cred-bank-name" value={form.bank_name} onChange={set("bank_name")} placeholder="Ex: Westlake, Lendbuzz, AFC..."
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Site (link do sistema)</label>
            <input data-testid="cred-website" value={form.website} onChange={set("website")} placeholder="https://dealer.banco.com"
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">Login / Usuário</label>
              <input data-testid="cred-username" value={form.username} onChange={set("username")}
                className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Senha</label>
              <input data-testid="cred-password" value={form.password} onChange={set("password")}
                className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 h-11 text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Observações</label>
            <textarea data-testid="cred-notes" value={form.notes} onChange={set("notes")} rows={2} placeholder="Ex: usar só pra financiamento acima de $10k"
              className="w-full bg-surface border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="border-t border-border p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-border hover:bg-surface">Cancelar</button>
          <button type="button" onClick={submit} disabled={saving} data-testid="cred-save"
            className="px-4 py-2 text-sm bg-primary text-white font-display font-bold uppercase tracking-wider hover:bg-primary-hover disabled:opacity-50">
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CredentialRow({ c, canEdit, onEdit, onDelete }) {
  const [show, setShow] = useState(false);
  return (
    <div data-testid={`cred-row-${c.id}`} className="border border-border bg-surface p-4 hover:border-primary/50 transition-colors">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-black uppercase text-sm tracking-wide">{c.bank_name}</p>
            {c.website && (
              <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer"
                data-testid={`cred-site-${c.id}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300">
                <ExternalLink size={10} /> Abrir site
              </a>
            )}
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[9px] uppercase tracking-widest text-text-secondary w-12 shrink-0">Login</span>
              <span className="text-sm font-mono truncate">{c.username || "—"}</span>
              {c.username && (
                <button type="button" data-testid={`cred-copy-user-${c.id}`} onClick={() => copyText(c.username, "Login")}
                  className="w-6 h-6 border border-border hover:border-primary hover:text-primary flex items-center justify-center shrink-0" title="Copiar login">
                  <Copy size={10} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[9px] uppercase tracking-widest text-text-secondary w-12 shrink-0">Senha</span>
              <span className="text-sm font-mono truncate" data-testid={`cred-pass-${c.id}`}>
                {c.password ? (show ? c.password : "••••••••") : "—"}
              </span>
              {c.password && (
                <>
                  <button type="button" data-testid={`cred-toggle-${c.id}`} onClick={() => setShow((v) => !v)}
                    className="w-6 h-6 border border-border hover:border-primary hover:text-primary flex items-center justify-center shrink-0" title={show ? "Esconder" : "Mostrar"}>
                    {show ? <EyeOff size={10} /> : <Eye size={10} />}
                  </button>
                  <button type="button" data-testid={`cred-copy-pass-${c.id}`} onClick={() => copyText(c.password, "Senha")}
                    className="w-6 h-6 border border-border hover:border-primary hover:text-primary flex items-center justify-center shrink-0" title="Copiar senha">
                    <Copy size={10} />
                  </button>
                </>
              )}
            </div>
          </div>
          {c.notes && <p className="mt-2 text-xs text-text-secondary leading-relaxed">{c.notes}</p>}
          <p className="mt-2 text-[9px] uppercase tracking-widest text-text-secondary/60">
            Atualizado por {c.updated_by || "—"}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-1 shrink-0">
            <button type="button" data-testid={`cred-edit-${c.id}`} onClick={() => onEdit(c)}
              className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center" title="Editar">
              <Edit2 size={13} />
            </button>
            <button type="button" data-testid={`cred-delete-${c.id}`} onClick={() => onDelete(c.id)}
              className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center" title="Excluir">
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PasswordsVault({ user }) {
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | cred (edit)
  const [search, setSearch] = useState("");
  const canEdit = ["owner", "gerente"].includes(user?.role);

  const reload = async () => {
    try {
      const r = await api.get("/bank-credentials");
      setCreds(r.data || []);
    } catch { /* 403 or network — leave empty */ }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Excluir este acesso?")) return;
    try {
      await api.delete(`/bank-credentials/${id}`);
      toast.success("Excluído");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? creds.filter((c) => `${c.bank_name} ${c.username} ${c.notes}`.toLowerCase().includes(q))
    : creds;

  return (
    <div data-testid="passwords-vault">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="label-eyebrow">Acessos da equipe</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">Senhas</h1>
          <p className="text-sm text-text-secondary mt-2 max-w-xl inline-flex items-center gap-1.5">
            <Lock size={12} className="shrink-0" />
            Logins dos sistemas de financiamento (bancos). Compartilhado entre as duas lojas — visível só pra dono, gerente e vendedores.
          </p>
        </div>
        {canEdit && (
          <button type="button" data-testid="add-credential" onClick={() => setEditing({})}
            className="bg-primary hover:bg-primary-hover text-white px-4 h-11 inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest transition-colors">
            <Plus size={14} /> Adicionar acesso
          </button>
        )}
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
        <input
          data-testid="cred-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar banco, login..."
          className="w-full bg-surface border border-border focus:border-primary focus:outline-none pl-9 pr-3 h-11 text-sm"
        />
      </div>

      {loading ? (
        <p className="text-sm text-text-secondary">Carregando...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border">
          <Key size={36} className="mx-auto text-text-secondary/30 mb-3" />
          <p className="text-text-secondary text-sm">
            {creds.length === 0 ? "Nenhum acesso cadastrado ainda." : "Nada encontrado na busca."}
          </p>
          {canEdit && creds.length === 0 && (
            <button type="button" onClick={() => setEditing({})} className="mt-4 text-xs uppercase tracking-widest text-primary hover:underline">
              + Cadastrar o primeiro banco
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filtered.map((c) => (
            <CredentialRow key={c.id} c={c} canEdit={canEdit} onEdit={setEditing} onDelete={onDelete} />
          ))}
        </div>
      )}

      {editing !== null && (
        <CredentialModal
          cred={editing?.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}
